import { useEffect, useState } from 'react';
import { pullFromServerWithTimeout, enableSync } from '../lib/sync.js';
import './AuthGate.css';

/**
 * 鉴权门。包在 App 外，未登录就拦截显示推门页。
 * - 启动时 GET /auth/status 探测当前会话
 * - 未登录 → 渲染密码输入；POST /auth/login 成功后放行
 * - 登录后 phase=syncing：从 /api/state 拉远端 state 覆盖本地，
 *   然后 enableSync()，再渲染 children。保证模块挂载时 localStorage 已是最新
 * - 后端 cookie httponly + samesite=lax，浏览器自动管理
 * - 通过 Vite dev proxy（/api 和 /auth 都转到 lingke.bond），浏览器视作同源，cookie 自动带
 */
export default function AuthGate({ children }) {
  const [state, setState] = useState({ phase: 'checking' });
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function authedHandoff() {
    // 拉远端，timeout 保险（网慢就用本地先工作）
    setState({ phase: 'syncing' });
    const r = await pullFromServerWithTimeout(3500);
    if (r?.timeout || r?.error) {
      // pull 失败：进入只读模式，绝对不能开 push——否则本地空数据会覆盖云端
      console.warn('[sync] pull failed/timeout, sync disabled this session (local-only)');
      setState({ phase: 'authed' });
      return;
    }
    // 拉到了跟本地不同的「视觉设定」（典型：清过缓存的设备本地空、服务器有底）——
    // 主题/壁纸/头像是开机命令式应用的，光更新 localStorage 不会重渲染，
    // 刷新一次让开机代码带着正确的值重新应用一遍（2026-07-30）。
    // 防循环：10s 内不重复触发；刷新后本地已=服务器，visualChanged 自然为 false。
    if (r?.visualChanged) {
      try {
        const last = +sessionStorage.getItem('lingke.visualRestoreAt') || 0;
        if (Date.now() - last > 10000) {
          sessionStorage.setItem('lingke.visualRestoreAt', String(Date.now()));
          window.location.reload();
          return; // 页面即将刷新，别再往下走
        }
      } catch { /* sessionStorage 不可用就跳过刷新，至多这次视觉没对上 */ }
    }
    enableSync();
    setState({ phase: 'authed' });
  }

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/auth/status', { credentials: 'include' });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const data = await res.json();
        if (data.setup_needed) {
          setState({ phase: 'setup_needed' });
        } else if (data.authenticated) {
          await authedHandoff();
        } else {
          setState({ phase: 'need_login' });
        }
      } catch (e) {
        setState({ phase: 'offline', msg: e.message });
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!password || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        await authedHandoff();
      } else {
        setError(translateError(data.error) || '密码不对，再试一次 ✦');
      }
    } catch (e) {
      setError('网络异常，再试一次');
    } finally {
      setSubmitting(false);
    }
  };

  if (state.phase === 'checking') {
    return (
      <div className="ag-bootstrap">
        <div className="ag-o-row">
          <span className="ag-o" />
          <span className="ag-o" />
          <span className="ag-o" />
        </div>
      </div>
    );
  }
  if (state.phase === 'syncing') {
    return (
      <div className="ag-bootstrap">
        <div className="ag-o-row">
          <span className="ag-o" />
          <span className="ag-o" />
          <span className="ag-o" />
        </div>
        <div className="ag-sync-msg">同步中 ……</div>
      </div>
    );
  }
  if (state.phase === 'authed') return children;

  return (
    <div className="ag-wrap">
      <div className="ag-card">
        <div className="ag-logo">língkè</div>
        <div className="ag-sub">私 人 记 忆 空 间</div>
        <div className="ag-rule" />

        {state.phase === 'offline' && (
          <div className="ag-error ag-offline">
            连不上记忆库 ——<br />
            <span className="ag-small">{state.msg}</span>
          </div>
        )}

        {state.phase === 'setup_needed' && (
          <div className="ag-error">
            后端还没初始化密码（OMBRE_DASHBOARD_PASSWORD 未设）。<br />
            <span className="ag-small">先去服务器设密码再回来。</span>
          </div>
        )}

        {(state.phase === 'need_login' || state.phase === 'offline') && (
          <form onSubmit={submit} className="ag-form">
            <input
              type="password"
              className="ag-input now-focus"
              placeholder="请输入密码……"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              autoComplete="current-password"
              disabled={submitting}
            />
            {error && <div className="ag-error">{error}</div>}
            <button
              type="submit"
              className="ag-btn"
              disabled={submitting || !password}
            >
              {submitting ? '正在轻叩门……' : '推 门'}
            </button>
          </form>
        )}

        <div className="ag-hint">只有铃能进来 ✦</div>
      </div>
    </div>
  );
}

function translateError(err) {
  if (!err) return '';
  // 后端返回的中英文错误信息归一化
  if (/unauthor/i.test(err) || /password/i.test(err)) return '密码不对，再试一次 ✦';
  return err;
}
